import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { Direction, ITravelSegment } from "../Models";
import { getDefaultHeaders, getSettings } from "../Settings";
import { LoadingState } from "../ApiCalls/Models";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { SpinnerSmall } from "../../Shared/Components/Spinner";
import { ITableBooking } from "./Model";
import dayjs from "dayjs";

const strings = getStrings().component.tableBooking;
const orderStrings = getStrings().component.orderSummary;
const commonStrings = getStrings().component.common;

export const BookTableModal = (props: { show: boolean, bookingNumber: string, direction: Direction, segment?: ITravelSegment, maxPassengerCount: number, onClosed: (result?: ITableBooking) => void }) => {

	const settings = getSettings();

	const [tider, setTider] = useState<string[]>();
	const [loadStatus, setLoadStatus] = useState<LoadingState>();
	const [bookStatus, setBookStatus] = useState<LoadingState>();
	const [selectedTime, setSelectedTime] = useState<string>("");
	const [selectedDay, setSelectedDay] = useState<string>();
	const [numberOfGuests, setNumberOfGuests] = useState<number>(props.maxPassengerCount);
	const [booking, setBooking] = useState<ITableBooking>();

	const departure = props.segment?.departure && dayjs(props.segment.departure);
	const arrival = props.segment?.arrival && dayjs(props.segment.arrival);

	const showFoodInfo = useMemo(() => {
		return tider && tider.some(x => dayjs(x, "HH:mm").hour() >= 11);
	}, [tider]);

	const hämtaLedigaTider = async () => {

		setLoadStatus("loading");
		setTider(null);

		const payload = {
			direction: props.direction,
			segmentId: props.segment.segmentId,
			serviceName: props.segment.serviceName,
			date: selectedDay,
			persons: numberOfGuests
		};

		const response = await fetch(`${settings.apiUrl}/tablebooking/booking/${props.bookingNumber}/findavailabletimes`, {
			method: "POST",
			credentials: "same-origin",
			headers: getDefaultHeaders(),
			body: JSON.stringify(payload)
		});

		if (response.ok) {
			var result = await response.json() as { items: string[] };
			setTider(result.items);
			setLoadStatus("loaded");
			return;
		}

		setLoadStatus("error");
	};

	const bookTable = async () => {
		setBookStatus("loading");

		const payload = {
			direction: props.direction,
			segmentId: props.segment.segmentId,
			serviceName: props.segment.serviceName,
			persons: numberOfGuests,
			time: selectedTime,
			date: selectedDay
		};

		const response = await fetch(`${settings.apiUrl}/tablebooking/booking/${props.bookingNumber}/booktable`, {
			method: "POST",
			credentials: "same-origin",
			headers: getDefaultHeaders(),
			body: JSON.stringify(payload)
		});

		if (response.ok) {
			setBookStatus("loaded");
			var result = await response.json() as ITableBooking;
			setBooking(result);
			return;
		}

		setBookStatus("error");

	};

	const reset = () => {
		setLoadStatus(null);
		setBookStatus(null);
		setTider(null);
		setSelectedTime(null);
		setBooking(null);
		setSelectedDay(null);
	};

	useEffect(() => {
		if (!props.segment)
			reset();

		return () => {
			reset();
		};
	}, [props.segment]);

	useEffect(() => {
		setNumberOfGuests(props.maxPassengerCount);
	}, [props.maxPassengerCount]);

	useEffect(() => {
		if (props.segment && numberOfGuests && selectedDay && loadStatus !== "loading")
			hämtaLedigaTider();

	}, [props.segment, numberOfGuests, selectedDay]);

	useEffect(() => {
		if (tider && selectedTime && !tider.some(x => x === selectedTime))
			setSelectedTime(null);
	}, [tider]);

	useEffect(() => {
		if (departure && !selectedDay)
			setSelectedDay(departure.format("YYYY-MM-DD"));
	}, [departure]);


	return <Modal show={props.show && !!props.segment} centered backdrop="static">
		<Modal.Header>
			<Modal.Title><i className="travelicon-KROG" />{strings.modalTitle}</Modal.Title>
		</Modal.Header>
		<Modal.Body className="book-table-dialog">

			<h4>{props.segment?.from.name} ➔ {props.segment?.to.name}</h4>


			<dl>
				<dt>{orderStrings.departure}</dt>
				<dd>{departure?.format("dddd D MMM, HH:mm")}</dd>
				<dt>{orderStrings.arrival}</dt>
				<dd>{arrival?.format("dddd D MMM, HH:mm")}</dd>
			</dl>

			{ departure?.isSame(arrival, "d") || <Form.Group controlId="day" className="select-day">
				<Form.Label>{strings.selectDay}</Form.Label>
				<Form.Control as="select" size="sm" custom value={selectedDay||""}
						onChange={e => setSelectedDay(e.currentTarget.value)}>
					<option value={departure?.format("YYYY-MM-DD")}>{departure?.format("dddd D MMM")}</option>
					<option value={arrival?.format("YYYY-MM-DD")}>{arrival?.format("dddd D MMM")}</option>
				</Form.Control>
			</Form.Group>}

			<Form.Group controlId="guestCount" className="select-persons">
				<Form.Label>{strings.numberOfGuests}</Form.Label>
				{ props.maxPassengerCount === 1 ? <span className="ml-3">1</span> :
					<Form.Control as="select" size="sm" custom value={numberOfGuests||0} disabled={props.maxPassengerCount === 1}
							onChange={e => setNumberOfGuests(parseInt(e.currentTarget.value))}>
						{Array.from({length: props.maxPassengerCount}).map((_, i) => <option value={i+1} key={i}>{i+1}</option>)}
					</Form.Control>
				}
			</Form.Group>

			{ loadStatus === "loading" && <Alert variant="light">{strings.fetchingTables} <SpinnerSmall /></Alert> }
			{ loadStatus === "error" && <Alert variant="danger"><strong>{commonStrings.error}!</strong> {strings.errorFetchingTables}</Alert> }

			{ loadStatus === "loaded" && bookStatus !== "loaded" && <>
				{ tider?.length > 0 ?
			  		<div>
						<strong>{strings.availableTimes}</strong>
						<div className="available-times mt-2">
							{ tider.map(x => <Button variant="outline-primary" className={`mr-2 mb-2 ${x === selectedTime ? "active" : ""}`} key={x} onClick={() => setSelectedTime(x)}>{x}</Button>) }
						</div>

						<Alert variant="info" className="mt-3" show={showFoodInfo}>{strings.foodInfo}</Alert>
						<Alert variant="warning" className="mt-3" show={numberOfGuests <= 2}>{strings.sharedTable}</Alert>
					</div> :
					<div className="alert alert-warning">{strings.noAvailableTimes}</div>
				}
			</>}

			<Alert variant="success" show={bookStatus === "error"}>{strings.errorBookingTable}</Alert>
			<Alert variant="success" show={bookStatus === "loaded"}>{strings.bookTableSuccess}</Alert>
		</Modal.Body>
		<Modal.Footer>
			{ bookStatus === "loaded" ?
				<Button variant="secondary" onClick={() => props.onClosed(booking)}>{commonStrings.close}</Button> :
				<>
					<Button variant="secondary" onClick={() => props.onClosed()}>{commonStrings.abort}</Button>
					<Button variant="primary" disabled={!selectedTime || bookStatus === "loading"} onClick={bookTable}>{strings.submitText} { bookStatus === "loading" && <SpinnerSmall />}</Button>
				</>
			}
		</Modal.Footer>

	</Modal>;
};